import React, { useState, useEffect, useMemo } from 'react';
import reportingFinancierApi from '../services/reporting-financier';
import useReportingData from '../hooks/useReportingData';
import UpdatingOverlay from './UpdatingOverlay';
import './ReportingPdrApv.css';
import './ReportingVN.css';

// Reporting Financier — onglets VN (véhicules neufs) ET VO (véhicules
// d'occasion). Composant générique piloté par la prop `type` ('vn' | 'vo') ;
// source = base snapshot `ventes_vn` / `ventes_vo` (cf. backend/src/routes/ventes.js).
// Les champs financiers (CA HT, marge, Roole, financement, CA TTC) sont
// résolus côté backend par type — cf. VENTES_CONFIG dans reporting-financier.js
// (VN marge=MC, VO marge=M1).
//
// KPIs nets (les lignes sens=-1 / annulations sont déduites) :
//   - Volume (nb de ventes net)
//   - CA HT (champ S11)
//   - Marge (champ MC)
//   - Taux de marge = marge / CA HT
//   - PV moyen HT, Marge moyenne / véhicule
//
// Présentation : KPI hero cards · comparaison N/N-1 (mois + cumul année) ·
// évolution mensuelle (CA + marge) · détail par marque (tableau triable) ·
// détail par concession.
//
// Réutilise le design system rpa-* (ReportingPdrApv.css). Filtres période +
// concession transmis par le shell (props). Le filtre marque est local.

// ─── Helpers de formatage (alignés sur ReportingPdrApv) ────────────────────
const fmtMoney = (v) => v == null
  ? '—'
  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtNumber = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
const fmtPct = (v) => v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v)} %`;

const fmtDelta = (pct) => {
  if (pct == null) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)} %`;
};

function defaultPeriode() {
  const now = new Date();
  const debut = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    date_debut: debut.toISOString().slice(0, 10),
    date_fin:   now.toISOString().slice(0, 10),
  };
}

// Badge delta (▲ vert / ▼ rouge) — vert = hausse (favorable pour ventes/CA).
function DeltaBadge({ pct }) {
  if (pct == null) return <span className="rpa-delta-empty">—</span>;
  const cls = pct > 0 ? 'rpa-delta-up' : pct < 0 ? 'rpa-delta-down' : 'rpa-delta-neutral';
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '◆';
  return (
    <span className={`rpa-delta ${cls}`}>
      <span className="rpa-delta-arrow">{arrow}</span>{fmtDelta(pct)}
    </span>
  );
}

// Carte KPI plate (label · valeur · delta N-1 + valeur N-1, ou sous-label).
function KpiHero({ label, value, valueN1, sublabel, delta }) {
  return (
    <div className="rpa-hero-card">
      <div className="rpa-hero-label">{label}</div>
      <div className="rpa-hero-value">{value}</div>
      <div className="rpa-hero-foot">
        {delta != null ? <DeltaBadge pct={delta} /> : (valueN1 != null ? <span className="rpa-delta-empty">—</span> : null)}
        <span className="rpa-hero-sublabel">
          {valueN1 != null ? <>vs <strong>{valueN1}</strong> (N-1)</> : (sublabel || ' ')}
        </span>
      </div>
    </div>
  );
}

// Tableau de comparaison N / N-1 (Mois en cours + Cumul année).
function ComparisonTable({ comparison, budget }) {
  if (!comparison) return null;
  const c = comparison;
  const bm = budget?.mois_budget;
  const bc = budget?.cumul_budget;
  const hasBudget = bm && bc && (bm.ca > 0 || bc.ca > 0);

  const rows = [
    { key: 'volume', label: 'Volume',   format: fmtNumber },
    { key: 'ca',     label: 'CA HT',    format: fmtMoney },
    { key: 'marge',  label: 'Marge',     format: fmtMoney },
    { key: 'taux_marge', label: 'Tx marge', format: fmtPct, noDelta: true, noBudget: true },
  ];

  const budgetDelta = (real, bgt) => {
    if (bgt == null || bgt === 0 || real == null) return null;
    return Math.round(((real - bgt) / bgt) * 10000) / 100;
  };

  const renderGroup = (realData, n1Data, deltas, budgetData) => (
    <>
      <th className="rvn-comp-col-n">Réalisé</th>
      <th>N-1</th>
      <th>Δ</th>
      {hasBudget && <><th className="rpa-comp-th-budget">Budget</th><th className="rpa-comp-th-budget">Δ Bgt</th></>}
    </>
  );

  return (
    <div className="rpa-comparison-block">
      <div className="rpa-block-header">
        <h3>Comparaison N / N-1 {hasBudget ? '/ Budget' : ''}</h3>
        <span className="rpa-block-sub">Ancrage : {new Date(c.anchor_date).toLocaleDateString('fr-FR')}</span>
      </div>
      <div className="rpa-comparison-table-wrap">
        <table className="rpa-comparison-table rpa-comp-split">
          <thead>
            <tr>
              <th rowSpan={2} className="rpa-comp-th-kpi"></th>
              <th colSpan={hasBudget ? 5 : 3} className="rpa-comp-th-group rpa-comp-group-mois">📅 Mois en cours</th>
              <th colSpan={hasBudget ? 5 : 3} className="rpa-comp-th-group rpa-comp-group-cumul">📊 Cumul année</th>
            </tr>
            <tr>
              {renderGroup()}
              {renderGroup()}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key}>
                <td className="rpa-comp-td-label">{row.label}</td>
                {/* Mois en cours */}
                <td className="rpa-comp-td-value rpa-comp-first">{row.format(c.mois_courant[row.key])}</td>
                <td className="rpa-comp-td-value rpa-comp-td-prev">{row.format(c.mois_n1[row.key])}</td>
                <td className="rpa-comp-td-delta">{row.noDelta ? '—' : <DeltaBadge pct={c.deltas.mois[row.key]} />}</td>
                {hasBudget && !row.noBudget && (
                  <>
                    <td className="rpa-comp-td-value rpa-comp-td-budget">{row.format(bm[row.key])}</td>
                    <td className="rpa-comp-td-delta"><DeltaBadge pct={budgetDelta(c.mois_courant[row.key], bm[row.key])} /></td>
                  </>
                )}
                {hasBudget && row.noBudget && <><td className="rpa-comp-td-budget">—</td><td>—</td></>}
                {/* Cumul année */}
                <td className="rpa-comp-td-value rpa-comp-first">{row.format(c.cumul_annee[row.key])}</td>
                <td className="rpa-comp-td-value rpa-comp-td-prev">{row.format(c.cumul_n1[row.key])}</td>
                <td className="rpa-comp-td-delta">{row.noDelta ? '—' : <DeltaBadge pct={c.deltas.cumul[row.key]} />}</td>
                {hasBudget && !row.noBudget && (
                  <>
                    <td className="rpa-comp-td-value rpa-comp-td-budget">{row.format(bc[row.key])}</td>
                    <td className="rpa-comp-td-delta"><DeltaBadge pct={budgetDelta(c.cumul_annee[row.key], bc[row.key])} /></td>
                  </>
                )}
                {hasBudget && row.noBudget && <><td className="rpa-comp-td-budget">—</td><td>—</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Mini-graphe d'évolution mensuelle : barres CA HT + ligne taux de marge.
function EvolutionChart({ points }) {
  if (!points || points.length === 0) {
    return <div className="rpa-empty">Aucune donnée sur la période.</div>;
  }
  const maxCa = Math.max(...points.map(p => p.ca), 1);
  const fmtMonth = (b) => {
    const [y, m] = b.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1)
      .toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  };
  return (
    <div className="rvn-evo">
      <div className="rvn-evo-bars">
        {points.map((p, i) => {
          const h = Math.max((p.ca / maxCa) * 100, 1);
          return (
            <div className="rvn-evo-col" key={i} title={`${fmtMonth(p.bucket)} · CA ${fmtMoney(p.ca)} · marge ${fmtMoney(p.marge)} · taux ${fmtPct(p.taux_marge)} · ${fmtNumber(p.volume)} ventes`}>
              <div className="rvn-evo-bar-wrap">
                <div className="rvn-evo-bar" style={{ height: `${h}%` }}>
                  <div className="rvn-evo-bar-marge" style={{ height: `${p.ca ? Math.min((p.marge / p.ca) * 100, 100) : 0}%` }} />
                </div>
              </div>
              <span className="rvn-evo-xlabel">{fmtMonth(p.bucket)}</span>
            </div>
          );
        })}
      </div>
      <div className="rvn-evo-legend">
        <span><i className="rvn-leg-ca" /> CA HT</span>
        <span><i className="rvn-leg-marge" /> Part marge</span>
      </div>
    </div>
  );
}

// Tableau de ventilation triable (par marque, par vendeur…). `entityLabel`
// titre la 1re colonne ; `defaultSort` = clé de tri initiale.
function VentilationTable({ rows, entityLabel = 'Marque', defaultSort = 'ca', showRoole = false, showFinancement = false, showPart = true }) {
  const [sortKey, setSortKey] = useState(defaultSort);
  const [sortDir, setSortDir] = useState('desc');

  const cols = [
    { key: 'label',         label: entityLabel,     align: 'left',  format: (v) => v },
    { key: 'volume',        label: 'Volume',        align: 'right', format: fmtNumber },
    { key: 'ca',            label: 'CA HT',         align: 'right', format: fmtMoney },
    ...(showPart ? [{ key: 'part', label: '% CA', align: 'right', format: fmtPct }] : []),
    { key: 'marge',         label: 'Marge',         align: 'right', format: fmtMoney },
    { key: 'taux_marge',    label: 'Taux marge',    align: 'right', format: fmtPct },
    ...(showRoole ? [{ key: 'taux_roole', label: 'Roole', align: 'right', format: fmtPct }] : []),
    ...(showFinancement ? [{ key: 'taux_financement', label: 'Financement', align: 'right', format: fmtPct }] : []),
    { key: 'pv_moyen',      label: 'PV moyen',      align: 'right', format: fmtMoney },
    { key: 'marge_moyenne', label: 'Marge / véh.',  align: 'right', format: fmtMoney },
  ];

  const totalCa = useMemo(() => rows.reduce((s, r) => s + (r.ca || 0), 0), [rows]);
  const enriched = useMemo(
    () => rows.map(r => ({ ...r, part: totalCa ? (r.ca / totalCa) * 100 : 0 })),
    [rows, totalCa]
  );
  const sorted = useMemo(() => {
    const arr = [...enriched];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return arr;
  }, [enriched, sortKey, sortDir]);

  const totals = useMemo(() => {
    const volume = rows.reduce((s, r) => s + (r.volume || 0), 0);
    const ca = totalCa;
    const marge = rows.reduce((s, r) => s + (r.marge || 0), 0);
    const roolePos = rows.reduce((s, r) => s + (r.roole_pos || 0), 0);
    const rooleNeg = rows.reduce((s, r) => s + (r.roole_neg || 0), 0);
    return {
      volume, ca, marge,
      part: 100,
      taux_marge: ca ? (marge / ca) * 100 : null,
      pv_moyen: volume ? ca / volume : null,
      marge_moyenne: volume ? marge / volume : null,
      // Net : (Roole souscrits − annulés) / volume net.
      taux_roole: volume ? ((roolePos - rooleNeg) / volume) * 100 : null,
      taux_financement: (() => {
        const caTtc = rows.reduce((s, r) => s + (r.ca_ttc || 0), 0);
        const fin   = rows.reduce((s, r) => s + (r.financement || 0), 0);
        return caTtc ? (fin / caTtc) * 100 : null;
      })(),
    };
  }, [rows, totalCa]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'label' ? 'asc' : 'desc'); }
  };

  if (!rows || rows.length === 0) {
    return <div className="rpa-empty">Aucune vente sur la période.</div>;
  }
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            {cols.map(col => (
              <th
                key={col.key}
                className={`${col.align === 'right' ? 'rvn-num' : ''} rvn-sortable ${sortKey === col.key ? 'rvn-sorted' : ''}`}
                onClick={() => onSort(col.key)}
              >
                {col.label}
                {sortKey === col.key && <span className="rvn-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i}>
              {cols.map(col => (
                <td key={col.key} className={col.align === 'right' ? 'rvn-num' : 'rvn-marque-cell'}>
                  {col.key === 'part'
                    ? (
                      <span className="rvn-part">
                        <span className="rvn-part-bar" style={{ width: `${Math.min(r.part, 100).toFixed(1)}%` }} />
                        <span className="rvn-part-txt">{fmtPct(r.part)}</span>
                      </span>
                    )
                    : col.format(r[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="rvn-total-row">
            <td>Total</td>
            <td className="rvn-num">{fmtNumber(totals.volume)}</td>
            <td className="rvn-num">{fmtMoney(totals.ca)}</td>
            {showPart && <td className="rvn-num">100 %</td>}
            <td className="rvn-num">{fmtMoney(totals.marge)}</td>
            <td className="rvn-num">{fmtPct(totals.taux_marge)}</td>
            {showRoole && <td className="rvn-num">{fmtPct(totals.taux_roole)}</td>}
            {showFinancement && <td className="rvn-num">{fmtPct(totals.taux_financement)}</td>}
            <td className="rvn-num">{fmtMoney(totals.pv_moyen)}</td>
            <td className="rvn-num">{fmtMoney(totals.marge_moyenne)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Tableau compact par établissement (entité commerciale sous les groupes CRM).
function EtablissementTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th>Société</th>
            <th className="rvn-num">Volume</th>
            <th className="rvn-num">CA HT</th>
            <th className="rvn-num">Marge</th>
            <th className="rvn-num">Taux marge</th>
            <th className="rvn-num">Roole</th>
            <th className="rvn-num">Financement</th>
            <th className="rvn-num">Marge / véh.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.label}</td>
              <td className="rvn-num">{fmtNumber(r.volume)}</td>
              <td className="rvn-num">{fmtMoney(r.ca)}</td>
              <td className="rvn-num">{fmtMoney(r.marge)}</td>
              <td className="rvn-num">{fmtPct(r.taux_marge)}</td>
              <td className="rvn-num">{fmtPct(r.taux_roole)}</td>
              <td className="rvn-num">{fmtPct(r.taux_financement)}</td>
              <td className="rvn-num">{fmtMoney(r.marge_moyenne)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────
// Tableau « Par mois » — évolution mensuelle de toutes les métriques VN/VO.
function MonthlyTable({ rows }) {
  if (!rows || rows.length === 0) return <div className="rpa-empty">Aucune donnée sur la période.</div>;
  const fmtMonth = (b) => {
    const [y, m] = String(b).split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  };
  const sorted = [...rows].sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)));
  return (
    <div className="rvn-table-wrap">
      <table className="rvn-table">
        <thead>
          <tr>
            <th>Mois</th>
            <th className="rvn-num">Volume</th>
            <th className="rvn-num">CA HT</th>
            <th className="rvn-num">Marge</th>
            <th className="rvn-num">Taux marge</th>
            <th className="rvn-num">PV moyen</th>
            <th className="rvn-num">Marge / véh.</th>
            <th className="rvn-num">Roole</th>
            <th className="rvn-num">Financement</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i}>
              <td>{fmtMonth(r.bucket)}</td>
              <td className="rvn-num">{fmtNumber(r.volume)}</td>
              <td className="rvn-num">{fmtMoney(r.ca)}</td>
              <td className="rvn-num">{fmtMoney(r.marge)}</td>
              <td className="rvn-num">{fmtPct(r.taux_marge)}</td>
              <td className="rvn-num">{fmtMoney(r.pv_moyen)}</td>
              <td className="rvn-num">{fmtMoney(r.marge_moyenne)}</td>
              <td className="rvn-num">{fmtPct(r.taux_roole)}</td>
              <td className="rvn-num">{fmtPct(r.taux_financement)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Bloc unique « Détail » : onglets Marque / Vendeur / Société / Mois (même pattern
// que le bloc « Parts de marché » de l'onglet Marché — .mar-monthly-toggle).
function VentilationTabs({ kpis }) {
  const hasEtab = (kpis.par_etablissement || []).length > 0;
  const hasTypeClient = (kpis.par_type_client || []).length > 0;
  const hasLoueurs = (kpis.par_loueurs || []).length > 0; // VN uniquement (backend)
  const hasMois = (kpis.evolution || []).length > 0;
  const [tab, setTab] = useState('marque'); // 'marque' | 'vendeur' | 'typeclient' | 'loueurs' | 'etablissement' | 'mois'
  const active =
    (tab === 'etablissement' && !hasEtab) ||
    (tab === 'typeclient' && !hasTypeClient) ||
    (tab === 'loueurs' && !hasLoueurs) ||
    (tab === 'mois' && !hasMois)
      ? 'marque' : tab;
  const n = (arr) => (kpis[arr] || []).length;
  const countSub =
    active === 'marque'     ? `${n('par_marque')} marque${n('par_marque') > 1 ? 's' : ''}`
    : active === 'vendeur'  ? `${n('par_vendeur')} vendeur${n('par_vendeur') > 1 ? 's' : ''}`
    : active === 'typeclient' ? `${n('par_type_client')} type${n('par_type_client') > 1 ? 's' : ''} de client`
    : active === 'loueurs'  ? `${n('par_loueurs')} loueur${n('par_loueurs') > 1 ? 's' : ''}`
    : active === 'mois'     ? `${n('evolution')} mois`
    : `${n('par_etablissement')} société${n('par_etablissement') > 1 ? 's' : ''}`;
  return (
    <div className="rpa-dim-block">
      <div className="rpa-block-header">
        <h3>Détail</h3>
        <span className="rpa-block-sub">{countSub}</span>
      </div>
      <div className="mar-monthly-toggle">
        <button className={active === 'marque' ? 'active' : ''} onClick={() => setTab('marque')}>Par marque</button>
        <button className={active === 'vendeur' ? 'active' : ''} onClick={() => setTab('vendeur')}>Par vendeur</button>
        {hasTypeClient && (
          <button className={active === 'typeclient' ? 'active' : ''} onClick={() => setTab('typeclient')}>Par type de client</button>
        )}
        {hasLoueurs && (
          <button className={active === 'loueurs' ? 'active' : ''} onClick={() => setTab('loueurs')}>Par loueur</button>
        )}
        {hasEtab && (
          <button className={active === 'etablissement' ? 'active' : ''} onClick={() => setTab('etablissement')}>Par société</button>
        )}
        {hasMois && (
          <button className={active === 'mois' ? 'active' : ''} onClick={() => setTab('mois')}>Par mois</button>
        )}
      </div>
      {active === 'marque' && <VentilationTable rows={kpis.par_marque || []} entityLabel="Marque" />}
      {active === 'vendeur' && <VentilationTable rows={kpis.par_vendeur || []} entityLabel="Vendeur" showRoole showFinancement showPart={false} />}
      {active === 'typeclient' && hasTypeClient && <VentilationTable rows={kpis.par_type_client} entityLabel="Type de client" />}
      {active === 'loueurs' && hasLoueurs && <VentilationTable rows={kpis.par_loueurs} entityLabel="Loueur" showRoole showFinancement showPart={false} />}
      {active === 'etablissement' && hasEtab && <EtablissementTable rows={kpis.par_etablissement} />}
      {active === 'mois' && hasMois && <MonthlyTable rows={kpis.evolution} />}
    </div>
  );
}

function ReportingVN({ type = 'vn', dateDebut, dateFin, concessions }) {
  const def = useMemo(() => defaultPeriode(), []);
  const effDateDebut = dateDebut || def.date_debut;
  const effDateFin   = dateFin   || def.date_fin;
  const effConcessions = useMemo(() => concessions || [], [concessions]);

  const [marque, setMarque]         = useState('');
  const [marques, setMarques]       = useState([]);

  useEffect(() => {
    reportingFinancierApi.getVentesDimensions(type)
      .then(r => setMarques(r.data?.marques || []))
      .catch(() => {});
  }, [type]);

  const filters = useMemo(() => ({
    date_debut: effDateDebut,
    date_fin:   effDateFin,
    ...(effConcessions.length ? { concession: effConcessions } : {}),
    ...(marque ? { marque } : {}),
  }), [effDateDebut, effDateFin, effConcessions, marque]);

  const compFilters = useMemo(() => ({
    date_fin: effDateFin,
    ...(effConcessions.length ? { concession: effConcessions } : {}),
    ...(marque ? { marque } : {}),
  }), [effDateFin, effConcessions, marque]);

  const { data, loading, error } = useReportingData(
    (signal) => Promise.all([
      reportingFinancierApi.getVentesKpis(type, filters, signal),
      reportingFinancierApi.getVentesComparison(type, compFilters, signal),
      reportingFinancierApi.getBudgetComparison({ service: type.toUpperCase(), date_fin: effDateFin, ...(effConcessions.length ? { concession: effConcessions } : {}) }, signal).catch(() => null),
    ]).then(([k, c, b]) => ({ kpis: k.data, comparison: c.data, budgetComp: b?.data || null })),
    [type, effDateDebut, effDateFin, JSON.stringify(effConcessions), marque],
    { enabled: !!(effDateDebut && effDateFin) },
  );
  const kpis       = data?.kpis || null;
  const comparison = data?.comparison || null;
  const budgetComp = data?.budgetComp || null;

  const g = kpis?.kpis_globaux || {};

  return (
    <div className="rpa-dashboard rf-surface">
      <UpdatingOverlay show={loading} />
      {/* Header */}
      <div className="rpa-dashboard-header">
        <div className="rpa-dashboard-title">
          <span className="rpa-dashboard-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 17h14M5 17a2 2 0 1 0 0 .01M19 17a2 2 0 1 0 0 .01"/>
              <path d="M3 13l2-5a2 2 0 0 1 1.9-1.4h10.2A2 2 0 0 1 19 8l2 5v3a1 1 0 0 1-1 1h-1M3 13v3a1 1 0 0 0 1 1h1M3 13h18"/>
            </svg>
          </span>
          <div>
            <h2>{type === 'vo' ? 'Véhicules d\'occasion' : 'Véhicules neufs'}</h2>
            <div className="rpa-dashboard-subtitle">
              Du {new Date(effDateDebut).toLocaleDateString('fr-FR')} au {new Date(effDateFin).toLocaleDateString('fr-FR')}
              {' · '}volumes, CA et marge nets des annulations
            </div>
          </div>
        </div>
        <div className="rpa-toolbar">
          <select value={marque} onChange={e => setMarque(e.target.value)} className={`page-select ${marque ? 'active-filter' : ''}`}>
            <option value="">Toutes marques</option>
            {marques.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {error && !kpis && <div className="rpa-error">⚠️ {error}</div>}

      {!kpis ? (
        <div className="rpa-loading">Chargement…</div>
      ) : (
        <>
          {/* KPI hero cards */}
          <div className="rpa-hero-grid">
            <KpiHero label="Volume (net)" value={fmtNumber(g.volume)} valueN1={g.volume_n1 != null ? fmtNumber(g.volume_n1) : null} delta={g.delta_volume_pct} />
            <KpiHero label="Chiffre d'affaires HT" value={fmtMoney(g.ca)} valueN1={g.ca_n1 != null ? fmtMoney(g.ca_n1) : null} delta={g.delta_ca_pct} />
            <KpiHero label="Marge" value={fmtMoney(g.marge)} valueN1={g.marge_n1 != null ? fmtMoney(g.marge_n1) : null} delta={g.delta_marge_pct} />
            <KpiHero label="Taux de marge" value={fmtPct(g.taux_marge)} valueN1={g.taux_marge_n1 != null ? fmtPct(g.taux_marge_n1) : null} delta={g.delta_taux_marge_pct} />
            <KpiHero label="PV moyen HT" value={fmtMoney(g.pv_moyen)} valueN1={g.pv_moyen_n1 != null ? fmtMoney(g.pv_moyen_n1) : null} delta={g.delta_pv_moyen_pct} />
            <KpiHero label="Marge moyenne" value={fmtMoney(g.marge_moyenne)} valueN1={g.marge_moyenne_n1 != null ? fmtMoney(g.marge_moyenne_n1) : null} delta={g.delta_marge_moyenne_pct} />
            <KpiHero label="Taux Roole" value={fmtPct(g.taux_roole)} valueN1={g.taux_roole_n1 != null ? fmtPct(g.taux_roole_n1) : null} delta={g.delta_taux_roole_pct} />
            <KpiHero label="Taux Financement" value={fmtPct(g.taux_financement)} valueN1={g.taux_financement_n1 != null ? fmtPct(g.taux_financement_n1) : null} delta={g.delta_taux_financement_pct} />
          </div>

          {/* Comparaison N/N-1 */}
          {comparison && <ComparisonTable comparison={comparison} budget={budgetComp} />}

          {/* Évolution mensuelle */}
          {kpis && (
            <div className="rpa-dim-block">
              <div className="rpa-block-header">
                <h3>Évolution mensuelle</h3>
                <span className="rpa-block-sub">CA HT par mois · part de marge</span>
              </div>
              <EvolutionChart points={kpis.evolution} />
            </div>
          )}

          {/* Détail — bloc unique à onglets (Marque / Vendeur / Établissement) */}
          {kpis && <VentilationTabs kpis={kpis} />}
        </>
      )}
    </div>
  );
}

export default ReportingVN;
